import { Button, ConfigProvider, Flex } from "antd";
import styled from "styled-components";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HEADER_HEIGHT } from "../../storyboardEditor/constants";
import { SlideLayout, TabKeys } from "../../storyboardEditor/types";
import { useTemplatesStatusStore } from "../../storyboardEditor/store/useTemplatesStatusStore";
import { ThemeSlideEditableTemplate } from "./ThemeSlideEditableTemplate";
import { ThemeLeftPanel } from "./ThemeLeftPanel";
import { useBoundStore } from "@/store";
import { LoadingSpinner } from "components/LoadingSpinner/";
import { useThemeData } from "../hooks/useThemeData";
import { useAntTheme } from "hooks/useAntTheme/";
import { useTranslate } from "hooks/useTranslate/";
import { PlusIcon } from "@phosphor-icons/react";
import placeholders from "../placeholders";
import { syncParameteredTextContentToSlidePlaceholders } from "../../storyboardEditor/helpers/itemsHelper";
import { SlideTemplatePreview } from "../../storyboardEditor/components/Template/SlideTemplatePreview";
import {
  useTemplateEditorStore,
  useTemplateEditorStoreApi,
} from "../../storyboardEditor/store/useTemplateEditorStore";
import { SlideType } from "pages/media/types/";
import { ThemeEditorMenu } from "./ThemeEditorMenu";
import { ResizableScenesLayout } from "../../storyboardEditor/components/ResizableScenesLayout";
import { CreateThemeModal } from "./CreateThemeModal";
import { PublishThemeModal } from "./PublishThemeModal";
import type { ThemeResponse } from "../hooks/useThemeApi";
import { useSaveLayout } from "../hooks/useThemeApi";
import { useTemplateAutoSave } from "../../storyboardEditor/hooks/useTemplateAutoSave";
import { useFitToPanelSize } from "../../storyboardEditor/hooks/useFitToPanelSize";
import { useItemCopyPaste } from "../../storyboardEditor/hooks/useItemCopyPaste";
import { getItems } from "../../storyboardEditor/helpers/mapTemplateStateToServerTemplate";
import { ITEM_ID_TO_DEFAULT_INPUT, ITEM_ID_TO_INPUT_KEY } from "../helpers";
import type { components } from "@libertify/api-types/media";
import { ThemeBulletPointsProvider } from "../ThemeBulletPointsContext";
import type { ThemeGroup } from "../../storyboardEditor/components/ThemeSelector";
import { EditorToolbar } from "../../storyboardEditor/components/common/EditorToolbar";
import { useParams } from "react-router-dom";
import { useGo } from "@refinedev/core";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { ThemeGallery } from "./themeGallery/ThemeGallery";
import { buildThemeSlideTemplateState } from "../buildThemeSlideTemplateState";

type LayoutInputs = NonNullable<
  components["schemas"]["UpsertLayoutTemplateRequest"]["inputs"]
>;

const noop = () => undefined;

// Left-panel sizing + resize handle mirror the storyboard editor.
const LEFT_PANEL_DEFAULT_PERCENT = 15;
const LEFT_PANEL_MIN_PERCENT = 15;
const LEFT_PANEL_MAX_PERCENT = 25;

const StyledLeftPanelGroup = styled(PanelGroup)`
  width: 100%;
  height: 100%;

  div[data-resize-handle-active="keyboard"],
  div[data-resize-handle-active="pointer"] {
    background: ${({ theme }) => theme.colorPrimary};
  }
`;

const ResizeHandleBar = styled.div`
  width: 1px;
  height: 100%;
  background: ${({ theme }) => theme.colorBorder};
  margin-inline: 1px;
`;

// Scroll viewport for the scene. When zoomed past the viewport the scene
// overflows here and pans in every direction; "safe center" keeps the
// top-left reachable instead of clipping it the way plain centering does.
const StyledSceneScroll = styled.div`
  display: flex;
  width: 100%;
  height: 100%;

  /* min-width:0 lets this flex child shrink to its track so horizontal overflow
     scrolls here instead of widening the panel. */
  min-width: 0;
  align-items: safe center;
  justify-content: safe center;
  overflow: auto;

  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colorBorder} transparent;
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colorBorder};
    border-radius: 3px;
  }
`;

export const ThemeEditor = () => {
  const {
    logo,
    logoRatio,
    activePresenter,
    presenterRatio,
    previewData: { previewSizes },
    mediaAspectRatio,
    publicThemes,
    orgThemes,
    selectedTheme,
    setSelectedTheme,
    isPublicTheme,
    organizationId,
    slidesPlaceholders,
    updateSlidePlaceholders,
    isThemesLoading,
  } = useThemeData();

  const { themeId } = useParams<{ themeId?: string }>();
  const go = useGo();

  // Tracks the theme id we've already synced into the editor so the route-sync
  // effect acts only on a real id change — never on unrelated re-renders.
  const syncedThemeIdRef = useRef<string | null>(null);

  const {
    currentSlide,
    setCurrentSlide,
    setTemplate,
    setTemplateState,
    templates,
    setCurrentItem,
    updateLogo,
  } = useTemplateEditorStore((state) => ({
    currentSlide: state.currentSlide,
    setCurrentSlide: state.setCurrentSlide,
    setTemplate: state.setTemplate,
    setTemplateState: state.setTemplateState,
    templates: state.templates,
    setCurrentItem: state.setCurrentItem,
    updateLogo: state.updateLogo,
  }));

  const setSidebarCollapsed = useBoundStore(
    (state) => state.setSidebarCollapsed
  );

  useEffect(() => {
    setSidebarCollapsed(true);
    return () => {
      setSidebarCollapsed(false);
    };
  }, [setSidebarCollapsed]);

  useEffect(() => {
    if (logo) {
      updateLogo({
        assetId: logo.asset_id,
        path: logo.path,
        showLogo: true,
      });
    }
  }, [logo, updateLogo]);

  const { theme } = useAntTheme();
  const t = useTranslate();

  const {
    setActiveTab,
    resetTemplateUpdates,
    resetBulkTemplateUpdates,
    setZoom,
  } = useTemplatesStatusStore((state) => ({
    setActiveTab: state.setActiveTab,
    resetTemplateUpdates: state.resetTemplateUpdates,
    resetBulkTemplateUpdates: state.resetBulkTemplateUpdates,
    setZoom: state.setZoom,
  }));
  const zoom = useTemplatesStatusStore((state) => state.zoom);

  useEffect(() => {
    // Discard a stale dirty-flag left over from an earlier, unrelated editor session in this tab
    resetTemplateUpdates();
    resetBulkTemplateUpdates();
  }, [resetTemplateUpdates, resetBulkTemplateUpdates]);

  useEffect(() => {
    return () => {
      // Reset zoom so it doesn't carry over to the video editor (shared store)
      setZoom(1);
    };
  }, [setZoom]);

  const templateScrollProps = useMemo(() => ({}), []);

  const currentSlideData = useMemo(() => {
    const placeholder = slidesPlaceholders.find(
      (slide) => slide.slide_type === (currentSlide as SlideType)
    );
    if (!placeholder) return undefined;
    return placeholder;
  }, [currentSlide, slidesPlaceholders]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);

  // Fit the largest box of the slide's aspect ratio into the editor area,
  // leaving a 16px margin on every side.
  const [editorEl, setEditorEl] = useState<HTMLElement | null>(null);
  const sceneAspectRatio =
    previewSizes && previewSizes.scene.height > 0
      ? previewSizes.scene.width / previewSizes.scene.height
      : 1;
  const editorFit = useFitToPanelSize(editorEl, sceneAspectRatio, 32, 32);
  const previewSize =
    editorFit.size.width > 0
      ? editorFit.size
      : (previewSizes?.scene ?? { width: 0, height: 0 });

  const { handleCopyItem, handlePasteItem, canCopy, canPaste } =
    useItemCopyPaste({
      previewWidth: previewSize.width,
      previewHeight: previewSize.height,
      registerShortcuts: true,
    });

  const templateEditorStoreApi = useTemplateEditorStoreApi();
  const { mutateAsync: saveLayoutMutate } = useSaveLayout(organizationId);

  const handleSave = useCallback(async () => {
    if (!selectedTheme || isPublicTheme) return;

    const { templateState, currentSlide: slideKey } =
      templateEditorStoreApi.getState();
    if (!slideKey) return;

    const existingLayout = selectedTheme.layouts[slideKey];
    const savedItems = existingLayout?.layout ?? [];
    const defaultItems =
      placeholders.DEFAULT_SLIDES_ITEMS[slideKey as SlideType];
    const savedNames = new Set(
      savedItems
        .map((item) => item.name)
        .filter((name): name is string => !!name)
    );
    const serverItems = [
      ...savedItems,
      ...defaultItems.filter(
        (item) => !!item.name && !savedNames.has(item.name)
      ),
    ];
    const items = getItems(
      templateState,
      mediaAspectRatio,
      serverItems,
      false,
      true
    );

    const inputs: LayoutInputs = { ...existingLayout?.inputs };

    const visibilityByItemId: Record<string, boolean> = {};
    templateState.parameteredTexts?.forEach((text) => {
      visibilityByItemId[text.id] = text.visible;
    });
    if (templateState.mainImage) {
      visibilityByItemId[templateState.mainImage.id] =
        templateState.mainImage.visible;
    }
    if (templateState.figure) {
      visibilityByItemId[templateState.figure.number.id] =
        templateState.figure.number.visible;
      if (templateState.figure.units) {
        visibilityByItemId[templateState.figure.units.id] =
          templateState.figure.number.visible &&
          templateState.figure.units.visible;
      }
    }

    Object.entries(ITEM_ID_TO_INPUT_KEY).forEach(([itemId, inputKey]) => {
      const visible = visibilityByItemId[itemId];
      if (visible === undefined) return;
      if (visible) {
        const defaultInput = ITEM_ID_TO_DEFAULT_INPUT[itemId];
        if (defaultInput && !(inputKey in inputs)) {
          inputs[inputKey] = defaultInput;
        }
      } else {
        delete inputs[inputKey];
      }
    });

    // Persist the slide's avatar shot so autopilot can render the avatar per
    // slide: null => no avatar on the slide, "Medium"/"CloseUp" => avatar shown
    // with that shot. Mirrors the storyboard mapping in
    // mapTemplateStateToServerTemplate.
    const avatarShot = templateState.presenter.presenter?.face_profile
      ? templateState.presenter.avatarShot
      : null;

    const result = await saveLayoutMutate({
      themeId: selectedTheme.id,
      slideType: slideKey as SlideType,
      data: {
        name: slideKey,
        layout: items,
        inputs,
        avatar_shot: avatarShot,
      },
    });

    setSelectedTheme({
      ...selectedTheme,
      layouts: {
        ...selectedTheme.layouts,
        [slideKey]: result,
      },
    });

    resetTemplateUpdates();

    const currentSlidePlaceholder = slidesPlaceholders.find(
      (s) => s.slide_type === slideKey
    );
    if (currentSlidePlaceholder) {
      const updatedSlide = syncParameteredTextContentToSlidePlaceholders(
        templateState.parameteredTexts,
        templateState.figure,
        currentSlidePlaceholder
      );
      updateSlidePlaceholders(
        slidesPlaceholders.map((s) =>
          s.slide_type === slideKey ? updatedSlide : s
        )
      );
    }
  }, [
    selectedTheme,
    isPublicTheme,
    templateEditorStoreApi,
    mediaAspectRatio,
    saveLayoutMutate,
    setSelectedTheme,
    resetTemplateUpdates,
    slidesPlaceholders,
    updateSlidePlaceholders,
  ]);

  useTemplateAutoSave(handleSave, handleSave);

  const saveIfPendingUpdates = useCallback(async () => {
    if (useTemplatesStatusStore.getState().updates > 0) {
      await handleSave();
    }
  }, [handleSave]);

  const handleSlideCardClick = useCallback(
    async (slideId: string) => {
      await saveIfPendingUpdates();
      setCurrentSlide(slideId);
      const template = templates.get(slideId);
      if (template) {
        setTemplateState(template);
      }
      setCurrentItem(undefined);
    },
    [
      saveIfPendingUpdates,
      setCurrentSlide,
      setTemplateState,
      templates,
      setCurrentItem,
    ]
  );

  const loadThemeIntoEditor = useCallback(
    (themeToLoad: ThemeResponse) => {
      const commonAssets = {
        brandKitData: { logo, audio: undefined },
        initialPresenter: activePresenter,
        presenterRatio,
        logoRatio,
        forceAdjustFont: true,
      };

      (Object.keys(placeholders.DEFAULT_SLIDES_ITEMS) as SlideType[]).forEach(
        (key) => {
          const state = buildThemeSlideTemplateState(
            themeToLoad,
            key,
            slidesPlaceholders,
            mediaAspectRatio,
            commonAssets
          );
          setTemplate(key, state);

          if (key === SlideLayout.TITLE) {
            setTemplateState(state);
            setCurrentSlide(key);
          }
        }
      );
    },
    [
      activePresenter,
      logo,
      logoRatio,
      mediaAspectRatio,
      presenterRatio,
      setTemplate,
      setTemplateState,
      setCurrentSlide,
      slidesPlaceholders,
    ]
  );

  const handleSelectTheme = useCallback(
    async (themeItem: ThemeResponse) => {
      await saveIfPendingUpdates();
      setSelectedTheme(themeItem);
      loadThemeIntoEditor(themeItem);
      if (!themeItem.public) {
        setActiveTab(TabKeys.themeInfo);
      }
    },
    [saveIfPendingUpdates, setSelectedTheme, loadThemeIntoEditor, setActiveTab]
  );

  const handleCreateNewTheme = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleThemeCreated = useCallback(
    (newTheme: ThemeResponse) => {
      setSelectedTheme(newTheme);
      loadThemeIntoEditor(newTheme);
      setActiveTab(TabKeys.themeInfo);
      // Mark as synced so the route-sync effect skips it — the theme isn't in
      // the cached lists yet, so it must not be treated as an unknown id.
      syncedThemeIdRef.current = newTheme.id;
      // Reflect the new theme in the URL so it survives reloads/back navigation.
      go({ to: `/theme-editor/${newTheme.id}` });
    },
    [setSelectedTheme, loadThemeIntoEditor, setActiveTab, go]
  );

  const handlePublished = useCallback(() => {
    setSelectedTheme((prev) => {
      if (!prev) return prev;
      return { ...prev, public: true };
    });
  }, [setSelectedTheme]);

  // Load the theme eagerly, then navigate. The gallery already holds the full
  // theme object, so hydrating the editor synchronously (instead of letting the
  // route-sync effect re-derive it from the URL after paint) lets the editor
  // render on the navigation frame — no spinner round-trip. Marking the id as
  // synced makes the route-sync effect skip it, exactly like handleThemeCreated.
  const handleGallerySelect = useCallback(
    async (theme: ThemeResponse) => {
      await saveIfPendingUpdates();
      syncedThemeIdRef.current = theme.id;
      setSelectedTheme(theme);
      loadThemeIntoEditor(theme);
      if (!theme.public) {
        setActiveTab(TabKeys.themeInfo);
      }
      go({ to: `/theme-editor/${theme.id}` });
    },
    [saveIfPendingUpdates, setSelectedTheme, loadThemeIntoEditor, setActiveTab, go]
  );

  // Dark, high-contrast back button sourced from theme tokens (white text on a
  // dark surface) so it tracks the active theme instead of hardcoded hex.
  const backButtonStyle = useMemo(
    () => ({
      color: theme.colorTextLightSolid,
      backgroundColor: theme.colorBgSolid,
      borderColor: theme.colorBgSolid,
      fontWeight: 500,
    }),
    [theme]
  );

  const handleBackToGallery = useCallback(async () => {
    // Keep the button in a loading state across the save + navigation so it
    // can't be clicked again while the pending update is still flushing.
    setIsNavigatingBack(true);
    try {
      await saveIfPendingUpdates();
      go({ to: "/theme-editor" });
    } finally {
      setIsNavigatingBack(false);
    }
  }, [saveIfPendingUpdates, go]);

  const themeGroups = useMemo((): ThemeGroup[] => {
    const groups: ThemeGroup[] = [];
    if (orgThemes.length > 0) {
      groups.push({
        key: "org",
        label: t("media.themeEditor.myThemes"),
        themes: orgThemes,
      });
    }
    if (publicThemes.length > 0) {
      groups.push({
        key: "public",
        label: t("media.themeEditor.publicThemesViewOnly"),
        themes: publicThemes,
      });
    }
    return groups;
  }, [publicThemes, orgThemes, t]);

  const routeTheme = useMemo(
    () =>
      themeId
        ? ([...orgThemes, ...publicThemes].find((th) => th.id === themeId) ??
          null)
        : null,
    [themeId, orgThemes, publicThemes]
  );

  // Sync the editor to the URL: /theme-editor/:themeId loads that theme,
  // /theme-editor clears the selection to show the gallery. This hydrates the
  // editor store from the route (a side effect). The ref guard makes it act
  // once per id change, so churn in callback/router identities or in-place
  // updates (save/publish) can never re-trigger the load.
  useEffect(() => {
    if (syncedThemeIdRef.current === (themeId ?? null)) return;

    if (!themeId) {
      syncedThemeIdRef.current = null;
      setSelectedTheme(null);
      return;
    }
    if (routeTheme) {
      syncedThemeIdRef.current = themeId;
      void handleSelectTheme(routeTheme);
      return;
    }
    // themeId in the URL but not in the loaded themes: wait while still
    // loading; otherwise fall back to the gallery (a just-created theme is
    // already loaded and is skipped via syncedThemeIdRef, not redirected).
    if (!isThemesLoading && selectedTheme?.id !== themeId) {
      go({ to: "/theme-editor", type: "replace" });
    }
  }, [
    themeId,
    routeTheme,
    selectedTheme,
    isThemesLoading,
    handleSelectTheme,
    setSelectedTheme,
    go,
  ]);

  if (!previewSizes) return <LoadingSpinner />;

  // Zoom is purely visual: scale the canonical scene size so the editable scene
  // and its item math grow together. Saved relative values are unaffected.
  const zoomedSceneWidth = Math.round(previewSize.width * zoom);
  const zoomedSceneHeight = Math.round(previewSize.height * zoom);

  // Single readiness gate for the whole editor view. selectedTheme lives in a
  // React context while the templates live in a separate (zustand) store, so
  // they can commit on different renders — gating the header chrome and the
  // body on the same flag prevents the chrome flashing in before the body.
  const isEditorReady = !!themeId && !!selectedTheme && templates.size > 0;

  return (
    <ConfigProvider theme={{ token: { fontFamily: "Inter, sans-serif" } }}>
      <Flex
        style={{
          height: "100vh",
          margin: -24,
          position: "relative",
          paddingBlockStart: HEADER_HEIGHT,
          fontFamily: "Inter, sans-serif",
        }}
        tabIndex={0}
      >
        {/* Header */}
        <Flex
          justify="space-between"
          align="center"
          style={{
            width: "100%",
            position: "absolute",
            height: HEADER_HEIGHT,
            backgroundColor: theme.colorBgBase,
            zIndex: 999,
            top: 0,
            left: 0,
            paddingInlineStart: 24,
            paddingInlineEnd: 24,
            borderBottom: `1px solid ${theme.colorBorderSecondary}`,
          }}
        >
          {/* Left: Back button while editing — the gallery is the selector. */}
          {isEditorReady ? (
            <Button
              icon={<ArrowLeftOutlined />}
              shape="round"
              loading={isNavigatingBack}
              onClick={() => void handleBackToGallery()}
              style={backButtonStyle}
            >
              {t("components.templates.index.back")}
            </Button>
          ) : (
            <span />
          )}
          {/* Center: Undo/Redo + copy/paste (only for private themes) */}
          {isEditorReady && !isPublicTheme && (
            <EditorToolbar
              onCopy={handleCopyItem}
              onPaste={handlePasteItem}
              canCopy={canCopy}
              canPaste={canPaste}
            />
          )}

          {/* Right: Create + Publish */}
          <Flex gap={16}>
            <Button
              type="dashed"
              onClick={handleCreateNewTheme}
              iconPosition="end"
              icon={<PlusIcon size={16} />}
              style={{
                color: theme.colorPrimary,
                borderColor: theme.colorPrimary,
              }}
            >
              {t("media.themeEditor.createNewTheme")}
            </Button>
            {isEditorReady && !isPublicTheme && (
              <Button type="primary" onClick={() => setPublishModalOpen(true)}>
                {t("media.themeEditor.publish")}
              </Button>
            )}
          </Flex>
        </Flex>

        {/* Main content */}
        {isEditorReady && (
          <ThemeBulletPointsProvider>
            {/* Resizable panels — mirrors the storyboard editor layout. */}
            <StyledLeftPanelGroup
              direction="horizontal"
              autoSaveId="theme-editor-left-panel"
            >
              <Panel
                id="theme-left-panel"
                order={1}
                defaultSize={LEFT_PANEL_DEFAULT_PERCENT}
                minSize={LEFT_PANEL_MIN_PERCENT}
                maxSize={LEFT_PANEL_MAX_PERCENT}
                style={{ display: "flex", flexDirection: "column" }}
              >
                <ThemeLeftPanel
                  slides={slidesPlaceholders}
                  templates={templates}
                  currentSlide={currentSlide}
                  mediaAspectRatio={mediaAspectRatio}
                  onSelectSlide={(slideType) =>
                    void handleSlideCardClick(slideType)
                  }
                />
              </Panel>
              <PanelResizeHandle>
                <ResizeHandleBar />
              </PanelResizeHandle>
              <Panel
                id="theme-content-area"
                order={2}
                style={{ display: "flex", flexDirection: "column" }}
              >
                {(() => {
                  const editorArea = (
                    <Flex
                      vertical
                      style={{
                        width: "100%",
                        position: "relative",
                        flex: 1,
                        // min-height:0 is required on every flex-column ancestor
                        // for the scene's vertical overflow to actually scroll.
                        minHeight: 0,
                      }}
                    >
                      <StyledSceneScroll ref={setEditorEl}>
                        {currentSlideData &&
                          (() => {
                            const currentTemplate = templates.get(
                              currentSlideData.slide_type
                            );
                            if (!currentTemplate) return null;
                            if (isPublicTheme) {
                              return (
                                <SlideTemplatePreview
                                  opacity={1}
                                  templateSize={{
                                    width: previewSize.width,
                                    height: previewSize.height,
                                  }}
                                  border={{}}
                                  onCardClick={noop}
                                  slide={currentSlideData}
                                  template={currentTemplate}
                                />
                              );
                            }

                            return (
                              <ThemeSlideEditableTemplate
                                previewWidth={zoomedSceneWidth}
                                previewHeight={zoomedSceneHeight}
                                templateScrollProps={templateScrollProps}
                                slide={currentSlideData}
                              />
                            );
                          })()}
                      </StyledSceneScroll>
                    </Flex>
                  );

                  // Public themes have no menu — render the editor area on its own.
                  if (isPublicTheme) return editorArea;

                  return (
                    <ResizableScenesLayout
                      autoSaveId="theme-editor-menu-panel"
                      menu={
                        <ThemeEditorMenu
                          previewWidth={zoomedSceneWidth}
                          previewHeight={zoomedSceneHeight}
                        />
                      }
                    >
                      {editorArea}
                    </ResizableScenesLayout>
                  );
                })()}
              </Panel>
            </StyledLeftPanelGroup>
          </ThemeBulletPointsProvider>
        )}

        {/* Theme gallery — list route (no theme in the URL) */}
        {!themeId && (
          <ThemeGallery
            themeGroups={themeGroups}
            onSelectTheme={handleGallerySelect}
          />
        )}

        {/* Loading a theme from the URL before its templates are ready */}
        {themeId && !isEditorReady && <LoadingSpinner />}

        {/* Modals */}
        <CreateThemeModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onCreated={handleThemeCreated}
          publicThemes={publicThemes}
          privateThemes={orgThemes}
        />

        {selectedTheme && !isPublicTheme && (
          <PublishThemeModal
            open={publishModalOpen}
            onClose={() => setPublishModalOpen(false)}
            theme={selectedTheme}
            onPublished={handlePublished}
          />
        )}
      </Flex>
    </ConfigProvider>
  );
};
